import * as React from 'react';
import {
  Box,
  Button,
  Chip,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SaveIcon from '@mui/icons-material/Save';
import RefreshIcon from '@mui/icons-material/Refresh';
import FileDownloadIcon from '@mui/icons-material/FileDownload';

import { QuickFilter, QuickFilterClear, QuickFilterControl, QuickFilterTrigger } from '@mui/x-data-grid';

import { FGridToolbarProps } from './FGridTypes';

export class FGridToolbar extends React.Component<FGridToolbarProps> {
  renderQuickFilter() {
    const { showQuickFilter } = this.props;
    if (!showQuickFilter) return null;

    return (
      <QuickFilter>
        <QuickFilterTrigger />
        <QuickFilterControl />
        <QuickFilterClear />
      </QuickFilter>
    );
  }

  render() {
    const {
      title = 'Data Grid',
      subtitle,
      addRow,
      saveAll,
      refresh,
      exportRows,
      saving,
      loading,
      total,
      dirtyCount,
      sx,
      actions = [],
    } = this.props;

    return (
      <Box
        sx={{
          backgroundColor: '#bbd4e6',
          borderBottom: '1px solid #d0d7de',
          px: 2,
          py: 1,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          minHeight: 52,
          ...sx,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ minWidth: 0 }}>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 700,
                color: '#16324f',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {typeof total === 'number' && <Chip size="small" label={`${total} records`} />}
          {typeof dirtyCount === 'number' && dirtyCount > 0 && (
            <Chip size="small" color="warning" label={`${dirtyCount} unsaved`} />
          )}
        </Stack>

        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
          {this.renderQuickFilter()}

          {refresh && (
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={refresh} disabled={loading || saving}>
                  <RefreshIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {exportRows && (
            <Tooltip title="Export">
              <span>
                <IconButton size="small" onClick={exportRows} disabled={loading || saving}>
                  <FileDownloadIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {actions.map((a) => (
            <Tooltip key={a.key} title={a.title || a.label}>
              <span>
                <Button
                  size="small"
                  variant={a.variant || 'outlined'}
                  color={a.color || 'primary'}
                  onClick={a.onClick}
                  disabled={a.disabled || loading || saving}
                  startIcon={a.icon}
                >
                  {a.label}
                </Button>
              </span>
            </Tooltip>
          ))}

          {addRow && (
            <Tooltip title="Add record">
              <span>
                <IconButton size="small" onClick={addRow} disabled={loading || saving}>
                  <AddIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}

          {saveAll && (
            <Tooltip title="Save changes">
              <span>
                <IconButton size="small" onClick={saveAll} disabled={loading || saving}>
                  <SaveIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
      </Box>
    );
  }
}

export default FGridToolbar;
